import { CommonModule } from '@angular/common';
import { Component, ElementRef, inject, ViewChild } from '@angular/core';
import {
  mapRawToBookings,
  mapToCsvPreviewBookings,
} from '../../core/import.utils';
import { ShipService } from '../../services/resources/ship.service';
import { forkJoin, take } from 'rxjs';
import { GuideService } from '../../services/resources/guide.service';
import {
  BusCompanyMap,
  JettyMap,
  StornoMap,
} from '../../core/bookingParameters';
import {
  Booking,
  CsvPreviewData,
  newGuideInterface,
  SourceMaps,
  UserInterface,
} from '../../core/models';
import { SimpleCheckModalComponent } from '../../common/modals/simple-check-modal.component';
import { BookingService } from '../../services/resources/booking.service';
import { AuthService } from '../../services/auth/auth.service';
import { CsvDataTableComponent } from './csv-data-table.component';
import { BookingPdfHtmlModalComponent } from '../../common/modals/booking-pdf-html-modal.component';
import { CsvTutorialComponent } from './csv-tutorial.component';
import { TourDetailService } from '../../services/resources/tour-detail.service';
import Papa from 'papaparse';
import { SimpleLoadingModalComponent } from '../../common/modals/simple-loading-modal.component';
import { SimpleConfirmModalComponent } from '../../common/modals/simple-confirm-modal.component';
import { BookingsToEventsPipe } from '../../core/bookings-to-events.pipe';

type RowObj = Record<string, string>;

@Component({
  selector: 'app-csvimport',
  imports: [
    CommonModule,
    SimpleCheckModalComponent,
    CsvDataTableComponent,
    BookingPdfHtmlModalComponent,
    CsvTutorialComponent,
    SimpleConfirmModalComponent,
    SimpleLoadingModalComponent,
  ],
  standalone: true,
  template: `
    <div class="mx-auto max-w-7xl  pt-6 sm:px-6 lg:px-8">
      <div class="bg-white rounded-lg shadow-md">
        <app-csv-tutorial></app-csv-tutorial>
      </div>
    </div>

    <div class="mx-auto max-w-7xl  pt-6 sm:px-6 lg:px-8">
      <div class="p-4 bg-white rounded-lg shadow-md">
        <div class="pb-4">
          <h2 class="text-xl font-semibold mb-4">Import RAW Data (CSV Only)</h2>
          <p class="mt-2 text-sm text-gray-700">Preview imported Raw Data</p>
        </div>
        <!-- File Input -->
        <label
          class="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-6 cursor-pointer hover:border-gray-400"
          for="fileUpload"
        >
          <svg
            class="w-12 h-12 text-gray-400 mb-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V7M16 3l-4 4m0 0L8 3m4 4V1"
            ></path>
          </svg>
          <span class="text-gray-600">Click to select a CSV file</span>
          <input
            #fileInput
            id="fileUpload"
            type="file"
            accept="text/csv"
            (click)="clearFileInput()"
            (change)="onFileSelected($event)"
            class="hidden"
          />
        </label>

        <!-- Preview Table -->
        <div *ngIf="previewData.length" class="mt-6 overflow-auto">
          <h3 class="text-lg font-medium mb-2">
            Preview ({{ previewLimit }} rows)
          </h3>
          <table class="min-w-full table-auto border-collapse">
            <thead>
              <tr>
                <th
                  *ngFor="let col of columns"
                  class="px-2 py-1 border text-left text-sm font-medium truncate"
                >
                  {{ col }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let row of previewData">
                <td
                  *ngFor="let col of columns"
                  class="px-2 py-1 border text-sm truncate"
                >
                  {{ row[col] }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Actions -->
        <div class="mt-4 flex space-x-2 w-full justify-between">
          <button
            (click)="reset()"
            class=" disabled:opacity-25 px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            [disabled]="!rawData.length"
          >
            Clear
          </button>
          <button
            (click)="preCheckData()"
            class=" disabled:opacity-25 px-4 py-2 bg-columbus-blue-600 text-white rounded hover:bg-columbus-blue-700"
            [disabled]="!rawData.length"
          >
            Convert Data
          </button>
        </div>
      </div>
    </div>

    <div
      *ngIf="conversionErrors?.length"
      class="mx-auto max-w-7xl pt-6 sm:px-6 lg:px-8"
    >
      <div
        class="bg-red-100 border-l-4 border-red-500 text-red-800 rounded-lg shadow-md"
      >
        <div class="p-4">
          <h2 class="text-xl font-semibold mb-2">Conversion Errors</h2>
          <p class="mb-4 text-sm text-red-700">
            The following errors occurred during CSV to booking conversion:
          </p>
          <ul role="list" class="space-y-4">
            <li
              *ngFor="let err of conversionErrors"
              class="flex justify-between items-center bg-white border border-red-300 shadow-sm rounded-md p-4"
            >
              <div class="flex items-center justify-start space-x-3">
                <div class="flex-shrink-0">
                  <span
                    class="inline-flex h-8 w-8 items-center justify-center rounded-full bg-red-500"
                  >
                    <svg
                      class="h-5 w-5 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M18 10c0 4.418-3.582 8-8 8s-8-3.582-8-8 3.582-8 8-8 8 3.582 8 8zm-8-4a1 1 0 00-1 1v3a1 1 0 102 0V7a1 1 0 00-1-1zm.002 8a1.25 1.25 0 110-2.5 1.25 1.25 0 010 2.5z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </span>
                </div>
                <div>
                  <p class="text-sm font-medium text-gray-800">
                    {{ err.message }}
                  </p>
                </div>
              </div>
              <div class="text-sm text-gray-500">Row {{ err.row }}</div>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div
      class="mx-auto max-w-7xl  pt-6 sm:px-6 lg:px-8"
      [class.pb-8]="validationErrors.length === 0"
    >
      <div class="bg-white rounded-lg shadow-md">
        <app-csv-data-table
          [bookings]="previewBookings"
          (selectBooking)="selectPrevBooking($event)"
          (importBookings)="onImportBookings($event)"
        ></app-csv-data-table>
      </div>
    </div>

    <div
      *ngIf="validationErrors?.length"
      class="mx-auto max-w-7xl pt-6 sm:px-6 lg:px-8 pb-8"
    >
      <div
        class="bg-red-100 border-l-4 border-red-500 text-red-800 rounded-lg shadow-md"
      >
        <div class="p-4">
          <h2 class="text-xl font-semibold mb-2">Validation Errors</h2>
          <p class="mb-4 text-sm text-red-700">
            The following validation errors occurred during booking check:
          </p>
          <ul role="list" class="space-y-4">
            <li
              *ngFor="let err of validationErrors"
              class="flex justify-between items-center bg-white border border-red-300 shadow-sm rounded-md p-4"
            >
              <div class="flex items-center justify-start space-x-3">
                <div class="flex-shrink-0">
                  <span
                    class="inline-flex h-8 w-8 items-center justify-center rounded-full bg-red-500"
                  >
                    <svg
                      class="h-5 w-5 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M18 10c0 4.418-3.582 8-8 8s-8-3.582-8-8 3.582-8 8-8 8 3.582 8 8zm-8-4a1 1 0 00-1 1v3a1 1 0 102 0V7a1 1 0 00-1-1zm.002 8a1.25 1.25 0 110-2.5 1.25 1.25 0 010 2.5z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </span>
                </div>
                <div>
                  <p class="text-sm font-medium text-gray-800">
                    {{ err.message }}
                  </p>
                </div>
              </div>
              <div class="text-sm text-gray-500">Row {{ err.row }}</div>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div *ngIf="importBookingToggler && loadedBookings.length > 0">
      <app-simple-check-modal
        [title]="'Import'"
        [message]="importBookingText"
        [positiveButtonText]="'Bestätigen'"
        [negativeButtonText]="'Abbrechen'"
        (close)="onImportModalClose($event)"
      />
    </div>

    <div *ngIf="prevBookingToggler && selectedPreviewBooking">
      <app-booking-pdf-html-modal
        [booking]="selectedPreviewBooking"
        (close)="onClosePreview()"
      ></app-booking-pdf-html-modal>
    </div>

    <div *ngIf="loadingLoadingToggler">
      <app-simple-loading-modal
        [title]="'Importing Data...'"
      ></app-simple-loading-modal>
    </div>
    <div *ngIf="loadingConfirmToggler">
      <app-simple-confirm-modal
        [title]="'Import Complete'"
        [confirmButtonText]="'OK'"
        (close)="toggleConfirmModal()"
      ></app-simple-confirm-modal>
    </div>
  `,
  styles: ``,
})
export class CsvimportComponent {
  @ViewChild('fileInput')
  fileInput!: ElementRef<HTMLInputElement>;

  /** Raw imported rows (as key-value objects) */
  /*
   rawData: Record<string, string>[] = [];
  columns: string[] = [];
  previewData: Record<string, string>[] = [];
  previewLimit = 10;
  */
  /** Columns detected from the CSV header */
  header_ONE: string[] = [];
  header_TWO: string[] = [];

  /** Subset used for preview */

  //papaparse stuff
  rawData: RowObj[] = [];
  columns: string[] = [];
  previewData: RowObj[] = [];
  previewLimit = 10;

  shipService = inject(ShipService);
  guideService = inject(GuideService);
  tourService = inject(TourDetailService);
  bookingService = inject(BookingService);
  authService = inject(AuthService);

  importBookingToggler: boolean = false;
  loadingConfirmToggler: boolean = false;
  loadingLoadingToggler: boolean = false;
  importBookingText: string = '';
  loadedBookings: Booking[] = [];

  previewBookings: CsvPreviewData[] = [];
  selectedPreviewBooking: Booking | undefined = undefined;
  prevBookingToggler: boolean = false;

  importBookings: CsvPreviewData[] = [];
  conversionErrors: { row: number; message: string }[] = [];
  validationErrors: { row: number; message: string }[] = [];

  toggleConfirmModal() {
    this.loadingConfirmToggler = !this.loadingConfirmToggler;
  }

  toggleLoadingModal() {
    this.loadingLoadingToggler = !this.loadingLoadingToggler;
  }

  onImportBookings(bookings: CsvPreviewData[]) {
    this.importBookings = bookings;
    this.importBookingText =
      'Do you want to import ' + this.importBookings.length + ' Bookings?';

    // console.log(this.loadedBookings);
    this.toggleImportModal();
  }

  selectPrevBooking(booking: CsvPreviewData) {
    this.selectedPreviewBooking = booking;
    this.onPrevBookingToggler();
  }

  onPrevBookingToggler() {
    this.prevBookingToggler = !this.prevBookingToggler;
  }

  onClosePreview() {
    this.selectedPreviewBooking = undefined;
    this.onPrevBookingToggler();
  }

  onFileSelected(evt: Event): void {
    // reset state for a fresh import
    this.previewBookings = [];
    this.importBookings = [];
    this.conversionErrors = [];
    this.validationErrors = [];

    const input = evt.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    Papa.parse<string[]>(file, {
      // delimiter: ',', // auto-detects too, but we set it explicitly
      newline: '\n', // handles \r\n as well
      quoteChar: '"',
      escapeChar: '"',
      skipEmptyLines: true,
      dynamicTyping: false,
      encoding: 'utf-8',
      worker: true,
      complete: (result) => {
        const rows = result.data as string[][];
        if (!rows || rows.length < 3) {
          // at least two header rows + one data row
          this.reset();
          return;
        }

        // 1) first two rows are headers
        const header_ONE = rows[0].map((h) => (h ?? '').trim());
        const header_TWO = rows[1].map((h) => (h ?? '').trim());

        // 2) merge headers into unique keys (same logic as before but robust)
        const mergedCols: string[] = [];
        let currentGroup = '';
        const maxLen = Math.max(header_ONE.length, header_TWO.length);

        for (let i = 0; i < maxLen; i++) {
          const g = header_ONE[i] ?? '';
          const d = header_TWO[i] ?? '';
          if (g) currentGroup = g;
          const key =
            currentGroup && d && currentGroup !== d
              ? `${currentGroup} ${d}`
              : d || currentGroup;
          mergedCols.push((key ?? '').trim());
        }
        this.columns = mergedCols;

        // 3) map all subsequent logical rows to objects by mergedCols
        const dataRows = rows.slice(2);
        const dateIdx = this.columns.indexOf('Booking Duration Datum');

        const logicalRows: RowObj[] = dataRows
          .filter((arr) => {
            // keep only rows with a date present
            const v = arr[dateIdx];
            return !!(v && String(v).trim().length);
          })
          .map((arr) => {
            const obj: RowObj = {};
            for (let i = 0; i < this.columns.length; i++) {
              const col = this.columns[i] ?? `col_${i}`;
              obj[col] = (arr[i] ?? '').trim();
            }
            return obj;
          });

        this.rawData = logicalRows;

        // 4) preview
        this.previewLimit = this.rawData.length;
        this.previewData = this.rawData.slice(0, this.previewLimit);

        // 5) clear native input so re-selecting same file works
        this.clearFileInput();

        // 6) proceed with your conversion using the robustly parsed data
        this.prepareData(this.rawData);
      },
      error: (err) => {
        console.error('Papa parse error', err);
      },
    });
  }

  /** Reset component state */
  reset(): void {
    this.header_ONE = [];
    this.header_TWO = [];
    this.rawData = [];
    this.previewData = [];
    this.previewBookings = [];
    this.selectedPreviewBooking = undefined;
    this.importBookings = [];
    this.conversionErrors = [];
    this.clearFileInput();
  }

  prepareData(data: Record<string, string>[]) {
    if (!data || data.length === 0) return;

    forkJoin({
      ships: this.shipService.getShips().pipe(take(1)),
      guides: this.guideService.getGuides().pipe(take(1)),
      tours: this.tourService.getTours().pipe(take(1)),
    }).subscribe(({ ships, guides, tours }) => {
      // build lookup maps
      const shipMap = new Map(ships.map((s) => [s.id, s]));
      const guideMap = new Map(guides.map((g) => [g.id, g]));
      const busCompanyMap = BusCompanyMap;
      const tourMap = new Map(tours.map((t) => [t.id, t]));
      const jettyMap = JettyMap;
      const stornoMap = StornoMap;

      const sourceMaps: SourceMaps = {
        shipMap,
        guideMap,
        busCompanyMap,
        tourMap,
        jettyMap,
        stornoMap,
      };

      const user = this.authService.getCurrentUser() as UserInterface;
      // now you have both maps, pass them into your pure mapper

      let conversionData:
        | { bookings: Booking[]; errors: { row: number; message: string }[] }
        | undefined = undefined;
      conversionData = mapRawToBookings(
        this.rawData,
        this.columns,
        sourceMaps,
        user,
      );
      this.loadedBookings = conversionData.bookings;
      this.conversionErrors = conversionData.errors;
    });
  }

  /** Clears the file input's value to ensure change event fires for the same file */
  clearFileInput(): void {
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  toggleImportModal() {
    this.importBookingToggler = !this.importBookingToggler;
  }

  isImporting = false;
  async onImportModalClose(value: boolean) {
    if (this.isImporting) {
      return;
    }
    //this.logTimesOfBookings();
    //this.isImporting = false;

    if (value && this.importBookings.length > 0) {
      this.isImporting = true;
      this.toggleLoadingModal();
      try {
        await this.bookingService.createBookingsBatch(
          this.importBookings as Booking[],
        );
        this.toggleConfirmModal();
        console.log('✅ Batch write succeeded for', this.importBookings.length);
      } catch (err) {
        console.error('🛑 Batch write failed', err);
      } finally {
        this.isImporting = false;

        this.toggleLoadingModal();
      }
    }

    this.toggleImportModal();
  }

  preCheckData() {
    if (this.loadedBookings) {
      this.previewBookings = mapToCsvPreviewBookings(this.loadedBookings);
      this.previewBookings.forEach((csvBooking) => {
        if (csvBooking.errors) {
          this.validationErrors.push(...csvBooking.errors);
        }
      });
    }
  }

  logTimesOfBookings() {
    this.importBookings.forEach((booking: Booking, index: number) => {
      console.log('booking index', index);
      console.log('date - ,', booking.duration.date);
      console.log('startime - ', booking.duration.startTime);
      console.log('endTime - ', booking.duration.endTime);
      for (const key in booking.guides) {
        console.log(
          'guide stellzeit - ',
          booking.guides[key].positionDetails.time,
        );
      }
    });
  }
}
